"use client";

import { Card, Link, useMediaQuery, useTheme } from "@mui/material";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

import emailValGirlLaptop from "@/assets/signup_process/email_val_girl_laptop.png";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import PageLayout from "@/components/templates/PageLayout";
import LOCAL_STORAGE from "@/constants/local-storage";
import ROUTE_URLS from "@/constants/route-urls";
import useAuth from "@/contexts/auth-context";
import useLanguage from "@/contexts/language-context";
import { getAccessToken } from "@/helpers/auth-helpers";
import { EmailValidationMessages } from "@/messages/emailValidation/email-validation.messages";
import authService from "@/services/auth.service";
import { BreadCrumb } from "@/types/page.types";

type Props = {
  messages: EmailValidationMessages;
};

export default function EmailValidationPage({ messages }: Props) {
  const auth = useAuth();
  const { lang } = useLanguage();
  const theme = useTheme();
  const router = useNavigate();
  const token = getAccessToken();

  useEffect(() => {
    if (auth.isEmailConfirmed) router(ROUTE_URLS.serviceDashboard(lang), { replace: true });
  }, [auth.isEmailConfirmed, lang, router]);

  useEffect(() => {
    const refreshToken = localStorage.getItem(LOCAL_STORAGE.refreshTokenKey);
    if (!refreshToken) return;
    // Falhas aqui não encerram a sessão: o refresh centralizado tenta novamente.
    authService.refreshToken().catch(() => undefined);
  }, [token]);


  const handleReturnToRegistration = () => {
    auth.logout(ROUTE_URLS.register(lang));
  };

  const handleResendEmail = () => {
    if (!auth.user) return;

    auth.sendConfirmationEmail(auth.user.id.toString());
  };

  const { verifyEmailTitle: title } = messages;
  const BREAD_CRUMBS: BreadCrumb[] = [{ title, href: ROUTE_URLS.emailValidation(lang) }];

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const typographyVariant = isSmallScreen ? "body1" : "h6";

  return (
    <PageLayout messages={messages} breadCrumbs={BREAD_CRUMBS} padding={1}>
      <PageContainer>
        <Card>
          <MDBox p={3} display="flex" flexDirection="row">
            <MDBox pr={3}>
              <MDTypography variant={isSmallScreen ? "h4" : "h3"} gutterBottom fontWeight="bold">
                {messages.verifyEmailTitle}
              </MDTypography>
              <MDTypography variant={typographyVariant}>
                {messages.verifyEmailSubtitle}
              </MDTypography>
              <MDTypography variant={isSmallScreen ? "body1" : "h4"} gutterBottom fontWeight="bold">
                {auth.user?.email}
              </MDTypography>
              <MDTypography variant={typographyVariant}>
                {messages.verifyEmailDescription}
              </MDTypography>
              <MDButton
                variant="contained"
                color="primary"
                onClick={handleResendEmail}
                sx={{ marginTop: theme.margins.xxxxs }}
              >
                {auth.isLoadingSendingEmail
                  ? auth.loadingIconSendingEmail
                  : messages.resendVerificationEmail}
              </MDButton>
              <MDBox width="100%" mt={2}>
                <MDTypography variant="body1">
                  {messages.wrongEmailAddress}{" "}
                  <Link
                    onClick={handleReturnToRegistration}
                    underline="hover"
                    variant="body1"
                    style={{ cursor: "pointer", color: "text", fontWeight: "bold" }}
                  >
                    {messages.returnToRegistration}
                  </Link>
                </MDTypography>
              </MDBox>
            </MDBox>

            {isLargeScreen && (
              <MDBox>
                <img
                  src={emailValGirlLaptop}
                  alt="Email Verification Illustration"
                  style={{ objectFit: "contain" }}
                  width={415}
                  height={415}
                />
              </MDBox>
            )}
          </MDBox>
        </Card>
      </PageContainer>
    </PageLayout>
  );
}
